import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  Briefcase,
  Building2,
  CheckCircle2,
  FileText,
  Linkedin,
  Loader2,
  Send,
  Upload,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBanner } from "@/components/ui/status-banner";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { EXPERIENCE_LABELS, type Experience } from "@/lib/types";

export const Route = createFileRoute("/apply/$searchId")({
  component: ApplyPage,
});

interface ApplySearch {
  search_id: string;
  puesto: string;
  experience: Experience;
  description: string | null;
  technologies: string[];
  stack: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  is_open: boolean;
  company_id: string;
  company_name: string;
  company_rubro: string | null;
}

const MAX_CV_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_CV_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];

function ApplyPage() {
  const { searchId } = Route.useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ["apply-search", searchId],
    queryFn: async (): Promise<ApplySearch | null> => {
      const { data, error } = await supabase.rpc("get_apply_search", {
        p_search_id: searchId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as ApplySearch) ?? null;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center dot-grid-bg">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center dot-grid-bg p-6">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 border border-destructive/30 mb-4">
            <X className="h-6 w-6 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight mb-2">
            Búsqueda no disponible
          </h1>
          <p className="text-sm text-muted-foreground">
            Esta búsqueda ya no está abierta o el link es inválido.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dot-grid-bg py-6 px-4">
      <div className="w-[80vw] mx-auto">
        <div className="text-center mb-2">
          <img
            src="/deepdive.png"
            alt="DeepDive"
            className="mx-auto h-56 w-auto select-none"
            draggable={false}
          />
        </div>
        <ApplyContent search={data} />
      </div>
    </div>
  );
}

function ApplyContent({ search }: { search: ApplySearch }) {
  const [submitted, setSubmitted] = useState(false);
  const [open, setOpen] = useState(false);

  const range = formatSalaryRange(
    search.salary_min,
    search.salary_max,
    search.salary_currency
  );
  const stack =
    search.stack ||
    (search.technologies.length > 0 ? search.technologies.join(", ") : null);

  if (submitted) {
    return (
      <>
        <Card className="p-10 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-success/10 border border-success/30 mb-4">
            <CheckCircle2 className="h-6 w-6 text-success" />
          </div>
          <h2 className="text-xl font-semibold tracking-tight mb-2">
            ¡Postulación enviada!
          </h2>
          <p className="text-sm text-muted-foreground">
            Recibimos tu información para <strong>{search.puesto}</strong> en{" "}
            <strong>{search.company_name}</strong>. Si encajás con el perfil te
            vamos a contactar al email que indicaste.
          </p>
        </Card>
        <p className="text-center text-[11px] text-muted-foreground mt-6">
          Powered by DeepDive
        </p>
      </>
    );
  }

  return (
    <>
      <Card className="p-8">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 border border-primary/30 shrink-0">
              <Briefcase className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-semibold tracking-tight">
                  {search.puesto}
                </h1>
                <Badge variant="muted" className="normal-case tracking-normal">
                  {EXPERIENCE_LABELS[search.experience]}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {search.company_name}
                {search.company_rubro && ` · ${search.company_rubro}`}
              </div>
            </div>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={() => setOpen(true)}
            className="shrink-0"
          >
            <Send className="h-4 w-4" />
            Postularme
          </Button>
        </div>

        {(stack || range) && (
          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm pt-6 border-t border-border">
            {stack && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  Stack
                </dt>
                <dd className="text-foreground">{stack}</dd>
              </div>
            )}
            {range && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  Rango salarial
                </dt>
                <dd className="text-foreground">{range}</dd>
              </div>
            )}
          </dl>
        )}

        {search.description && (
          <div className="mt-6 pt-6 border-t border-border">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Sobre el puesto
            </dt>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {search.description}
            </p>
          </div>
        )}
      </Card>

      <p className="text-center text-[11px] text-muted-foreground mt-6">
        Powered by DeepDive
      </p>

      <ApplyDialog
        open={open}
        onOpenChange={setOpen}
        search={search}
        onSubmitted={() => {
          setOpen(false);
          setSubmitted(true);
        }}
      />
    </>
  );
}

function ApplyDialog({
  open,
  onOpenChange,
  search,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  search: ApplySearch;
  onSubmitted: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [cv, setCv] = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    kind: "error";
    title: string;
    detail?: string;
  } | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error("Falta el nombre.");
      if (!email.trim()) throw new Error("Falta el email.");
      if (!linkedinUrl.trim() && !cv) {
        throw new Error("Cargá tu CV o dejá un link a LinkedIn (o ambos).");
      }

      let cvPath: string | null = null;
      if (cv) {
        const ext = cv.name.split(".").pop() ?? "pdf";
        const safeName = name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        cvPath = `${search.search_id}/${Date.now()}-${safeName || "cv"}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("cvs")
          .upload(cvPath, cv, {
            contentType: cv.type || undefined,
            upsert: false,
          });
        if (upErr) throw new Error(`No se pudo subir el CV: ${upErr.message}`);
      }

      const { error: insErr } = await supabase.from("applications").insert({
        search_id: search.search_id,
        company_id: search.company_id,
        candidate_name: name.trim(),
        candidate_email: email.trim(),
        linkedin_url: linkedinUrl.trim() || null,
        cv_url: cvPath,
        cover_letter: coverLetter.trim() || null,
      });
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      setFeedback(null);
      onSubmitted();
    },
    onError: (e: Error) => {
      setFeedback({
        kind: "error",
        title: "No se pudo enviar tu postulación",
        detail: e.message,
      });
    },
  });

  function onCvChange(file: File | null) {
    setCvError(null);
    if (!file) {
      setCv(null);
      return;
    }
    if (file.size > MAX_CV_BYTES) {
      setCvError("El archivo supera los 5 MB.");
      return;
    }
    if (!ALLOWED_CV_TYPES.includes(file.type)) {
      setCvError("Formato no permitido. Subí PDF, DOC, DOCX o TXT.");
      return;
    }
    setCv(file);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Postulate a {search.puesto}</DialogTitle>
          <DialogDescription>
            Contanos quién sos y por qué te interesa. Si encajás con el perfil
            te contactamos.
          </DialogDescription>
        </DialogHeader>

        {feedback && (
          <StatusBanner
            variant={feedback.kind}
            title={feedback.title}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.detail}
          </StatusBanner>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="name">Nombre completo *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="linkedin">
              <span className="inline-flex items-center gap-1.5">
                <Linkedin className="h-3.5 w-3.5" />
                Perfil de LinkedIn
              </span>
            </Label>
            <Input
              id="linkedin"
              type="url"
              placeholder="https://www.linkedin.com/in/..."
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cv">
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                CV (PDF, DOC, DOCX o TXT — máx 5 MB)
              </span>
            </Label>
            <CvPicker file={cv} onChange={onCvChange} error={cvError} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cover">¿Por qué te interesa?</Label>
            <Textarea
              id="cover"
              rows={4}
              placeholder="Contanos brevemente por qué te interesa esta búsqueda."
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
            />
          </div>

          <p className="text-[11px] text-muted-foreground">
            Dejá tu CV o el link a LinkedIn — necesitamos al menos uno.
          </p>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={submit.isPending}
            className="w-full"
          >
            Enviar postulación
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CvPicker({
  file,
  onChange,
  error,
}: {
  file: File | null;
  onChange: (f: File | null) => void;
  error: string | null;
}) {
  return (
    <div>
      <label
        className={cn(
          "flex items-center gap-3 rounded-md border border-dashed bg-card/60 px-4 py-3 cursor-pointer transition-colors",
          error
            ? "border-destructive/50 hover:border-destructive/70"
            : "border-input hover:border-primary/60 hover:bg-card"
        )}
      >
        <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0 flex-1 text-sm">
          {file ? (
            <>
              <div className="font-medium truncate">{file.name}</div>
              <div className="text-xs text-muted-foreground">
                {(file.size / 1024).toFixed(0)} KB · clickeá para cambiar
              </div>
            </>
          ) : (
            <span className="text-muted-foreground">
              Click para subir tu CV
            </span>
          )}
        </div>
        {file && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onChange(null);
            }}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-accent shrink-0"
            aria-label="Quitar archivo"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <input
          type="file"
          className="sr-only"
          accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        />
      </label>
      {error && (
        <p className="text-xs text-destructive mt-1.5">{error}</p>
      )}
    </div>
  );
}

function formatSalaryRange(
  min: number | null,
  max: number | null,
  currency: string
): string | null {
  if (!min && !max) return null;
  const fmt = (n: number) => new Intl.NumberFormat("es-AR").format(n);
  if (min && max) return `${currency} ${fmt(min)} - ${fmt(max)}`;
  if (min) return `desde ${currency} ${fmt(min)}`;
  if (max) return `hasta ${currency} ${fmt(max)}`;
  return null;
}
